import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminNav from "../components/AdminNav";
import RequireAdmin from "../components/RequireAdmin";
import Pagination from "../components/Pagination";
import {
  moderateSubmission,
  moderationQueue,
  requeueVideo,
} from "../lib/api";

export default function AdminModerationPage() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(async () => {
    setState("loading");
    try {
      const d = await moderationQueue({ status: statusFilter, page });
      setData(d);
      setState("ready");
    } catch (e) {
      setState("error");
    }
  }, [statusFilter, page]);
  useEffect(() => { load(); }, [load]);

  async function decide(id, status) {
    const note = status === "rejected"
      ? window.prompt("Reason (shown to the owner, optional):") || ""
      : "";
    try {
      await moderateSubmission(id, status, note);
      toast.success(`Submission ${status}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Moderation failed");
    }
  }

  async function retryVideo(id) {
    try {
      await requeueVideo(id);
      toast.success("Video re-queued for transcode.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Requeue failed");
    }
  }

  return (
    <RequireAdmin>
      <div
        data-testid="admin-moderation"
        className="mx-auto w-full max-w-[1200px] px-5 py-8 md:px-gutter md:py-10"
      >
        <Breadcrumbs items={[
          { label: "Home", to: "/" },
          { label: "Admin", to: "/admin" },
          { label: "Moderation" },
        ]} />
        <h1 className="mt-6 text-title-page text-ink-text">Moderation</h1>
        <AdminNav />

        <div className="mb-4 flex gap-2">
          {["pending", "approved", "rejected"].map((s) => (
            <button
              key={s}
              data-testid={`moderation-filter-${s}`}
              onClick={() => { setPage(1); setStatusFilter(s); }}
              className={
                "rounded-chip border px-3 py-1.5 text-meta " +
                (statusFilter === s
                  ? "border-indigo-text bg-indigo-chip-bg text-indigo-chip-text"
                  : "border-ink-chip bg-ink-surface text-ink-muted hover:text-ink-text")
              }
            >
              {s} {data && s === statusFilter ? `· ${data.total}` : ""}
            </button>
          ))}
        </div>

        {state === "loading" && <div data-testid="moderation-loading" className="text-body text-ink-muted">Loading…</div>}
        {state === "error" && <div data-testid="moderation-error" className="text-body text-ink-muted">Failed to load.</div>}
        {state === "ready" && data && (
          <>
            {data.results.length === 0 ? (
              <div data-testid="moderation-empty" className="rounded-card border border-ink-chip bg-ink-surface p-6 text-body text-ink-muted">
                Nothing in the {statusFilter} queue right now.
              </div>
            ) : (
              <ul className="space-y-4" data-testid="moderation-list">
                {data.results.map((r) => (
                  <ModerationRow
                    key={r.id}
                    row={r}
                    onApprove={() => decide(r.id, "approved")}
                    onReject={() => decide(r.id, "rejected")}
                    onRequeue={() => retryVideo(r.id)}
                  />
                ))}
              </ul>
            )}
            <Pagination
              page={data.page}
              totalPages={data.total_pages}
              onChange={setPage}
              testId="moderation-pagination"
            />
          </>
        )}
      </div>
    </RequireAdmin>
  );
}

function ModerationRow({ row, onApprove, onReject, onRequeue }) {
  const transcodeStatus = row.video?.transcode_status;
  const canRequeue = row.video && (transcodeStatus === "failed"
    || transcodeStatus === "queued");
  return (
    <li
      data-testid={`moderation-row-${row.id}`}
      className="flex flex-col gap-4 rounded-card border border-ink-chip bg-ink-surface p-4 md:flex-row"
    >
      <div className="w-full md:w-[220px]">
        {row.image?.thumbnail_url || row.image?.url ? (
          <img
            data-testid={`moderation-row-image-${row.id}`}
            src={row.image.thumbnail_url || row.image.url}
            alt=""
            className="w-full rounded-chip object-cover"
            style={{ aspectRatio: "5 / 7" }}
          />
        ) : row.video ? (
          <div className="rounded-chip border border-ink-chip bg-ink-bg p-3 text-meta text-ink-muted">
            <div className="tabular">Video · {transcodeStatus || "queued"}</div>
            {row.video.poster_url && (
              <img src={row.video.poster_url} alt="" className="mt-2 w-full rounded-chip" />
            )}
          </div>
        ) : (
          <div className="text-meta text-ink-muted">no media</div>
        )}
      </div>
      <div className="flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <div className="text-title-section text-ink-text">
              {row.card?.name || "?"}
              {row.variant_slug && row.variant_slug !== "normal" && (
                <span className="ml-2 text-body text-ink-muted">
                  · {_variantLabel(row.variant_slug)}
                </span>
              )}
              <span className="ml-2 text-meta text-ink-muted tabular">
                #{row.card?.number || "?"}
              </span>
            </div>
            <div className="mt-1 text-meta text-ink-muted">
              <Link
                data-testid={`moderation-row-user-${row.id}`}
                to={`/users/${row.user?.id || ""}`}
                className="text-ink-text hover:text-indigo-text"
              >
                {row.user?.display_name || "?"}
              </Link>
              <span className="mx-2 text-ink-chip">·</span>
              <span className="tabular">{row.created_at?.slice(0, 10)}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid={`moderation-approve-${row.id}`}
              onClick={onApprove}
              className="rounded-card bg-[#4353E8] px-4 py-2 text-body font-semibold text-white transition-transform duration-cc ease-cc-ease hover:translate-y-[-1px]"
            >
              Approve
            </button>
            <button
              data-testid={`moderation-reject-${row.id}`}
              onClick={onReject}
              className="rounded-card border border-ink-chip bg-ink-surface px-4 py-2 text-body text-ink-muted hover:text-ink-text"
            >
              Reject
            </button>
            {canRequeue && (
              <button
                data-testid={`moderation-requeue-${row.id}`}
                onClick={onRequeue}
                className="rounded-card border border-ink-chip bg-ink-surface px-4 py-2 text-meta text-ink-muted hover:text-ink-text"
              >
                Re-queue video
              </button>
            )}
          </div>
        </div>
        <Link
          to={`/cards/${row.card?.external_id || ""}/${row.variant_slug || "normal"}`}
          className="mt-2 inline-block text-meta text-indigo-text hover:underline"
        >
          View card page →
        </Link>
      </div>
    </li>
  );
}

function _variantLabel(slug) {
  return {
    normal: null,
    holo: "Holo",
    "reverse-holo": "Reverse Holo",
    "first-edition": "1st Edition",
    promo: "Promo",
  }[slug] || slug;
}
